'use client'

import AbcFilter from '@/components/filter/NewAbcFilter'
import Filter from '@/components/filter/NewFilter'
import { ComponentType } from 'react'
import { useCreateFilterPathFunc } from 'shared/UseCreateFilterPathFunc'
import { isTabletScreen } from 'shared/domHelpers'
import { Facet, FacetValue, FacetValueGroup } from 'types'
// import { SearchOverlay } from '@/components/search/SearchOverlay'
// import { Search } from 'react-feather'
// import Button from '@/components/ui-other/basic/Button'

const translateFacetKey = (key: string): string => {
  switch (key) {
    case 'organization':
      return 'Verband'
    case 'season':
      return 'Saison'
    case 'day':
      return 'Tag'
    case 'week':
      return 'Woche'
    case 'tournament':
      return 'Wettbewerb'
    case 'phase':
      return 'Phase'
    case 'round':
      return 'Spieltag'
    case 'type':
      return 'Tabellentyp'
    case 'gender':
      return 'Geschlecht'
    case 'ageGroup':
      return 'Altersgruppe'
    default:
      return ''
  }
}

const getTotalCountType = (type: string, totalCount?: number) => {
  switch (type) {
    case 'leagues':
      return totalCount === 1 ? 'Liga' : 'Ligen'
    case 'tournaments':
      return totalCount === 1 ? 'Wettbewerb' : 'Wettbewerbe'
    case 'fields':
      return totalCount === 1 ? 'Halle' : 'Hallen'
    case 'clubs':
      return totalCount === 1 ? 'Verein' : 'Vereine'
    case 'teams':
      return totalCount === 1 ? 'Team' : 'Teams'
    case 'games':
      return totalCount === 1 ? 'Spiel' : 'Spiele'
    default:
      return ''
  }
}

// Define all possible filter keys, needed for the filter area
const ALL_POSSIBLE_FILTERS = [
  'organization',
  'season',
  'day',
  'week',
  'tournament',
  'phase',
  'round',
  'type',
  'gender',
  'ageGroup',
  'letter'
] as const

export const NewFilterArea: ComponentType<{
  basePath: string
  facets?: Facet[]
  omitFilters?: string[]
  totalCount?: number
  totalCountType?:
    | 'leagues'
    | 'tournaments'
    | 'fields'
    | 'clubs'
    | 'teams'
    | 'games'
}> = ({ basePath, facets, omitFilters, totalCount, totalCountType }) => {
  const isTablet = isTabletScreen()
  if (!facets) return null

  // Always create the same number of path functions
  const allPathFuncs = Object.fromEntries(
    ALL_POSSIBLE_FILTERS.map((filterKey) => [
      filterKey,
      useCreateFilterPathFunc(basePath, filterKey)
    ])
  )

  const usedFacets =
    omitFilters && omitFilters.length
      ? facets.filter((f) => !omitFilters.includes(f.key))
      : facets

  const letterFacet = usedFacets.find((f) => f.key === 'letter')

  const otherFilters = usedFacets
    .filter((f) => (letterFacet ? f.key !== letterFacet.key : true))
    .filter((f) => ALL_POSSIBLE_FILTERS.includes(f.key as any))
    .map((of) => ({
      ...of,
      pathFunc: allPathFuncs[of.key as keyof typeof allPathFuncs]
    }))

  if (!letterFacet && otherFilters.length === 0) return null

  return (
    <>
      <div className="filterarea">
        <div
          className={
            'sm:flex-col items-start uppercase space-x-2 sm:space-x-0 sm:space-y-4'
          }
        >
          <div className="space-y-2 sm:space-y-0 w-full">
            {(otherFilters.length > 0 || letterFacet) && (
              <div className="grid grid-cols-2 gap-4 space-y-2 sm:space-y-0 ligen-filter">
                {otherFilters.map(
                  ({ key, values, pathFunc, removable, clearOnChange }, i) => (
                    <Filter
                      key={key}
                      title={translateFacetKey(key)}
                      removable={removable}
                      links={values.map((v) => ({
                        text: v.name,
                        addFilterPath: pathFunc.addFilter(v.id, clearOnChange),
                        removeFilterPath: pathFunc.removeFilter(clearOnChange),
                        selected: getSelected(v)
                      }))}
                    />
                  )
                )}
                {letterFacet && isTablet && (
                  <AbcFilter basePath={basePath} facet={letterFacet} />
                )}
              </div>
            )}
            {<div className="view-vertical justify-between"></div>}
            {letterFacet && !isTablet && (
              <AbcFilter basePath={basePath} facet={letterFacet} />
            )}
          </div>
        </div>
      </div>
      <div className="view-horizontal gap-x-2 items-start my-6">
        {totalCountType && (
          <div className=" text-sm font-bold border-b-2 border-primary truncate overflow-hidden pb-2 pt-3 sm:pt-0 w-auto inline-block">
            {totalCount === 0 || !totalCount ? 'Keine' : totalCount}{' '}
            {getTotalCountType(totalCountType, totalCount)}
          </div>
        )}
      </div>
    </>
  )
}

function getSelected(value: FacetValue | FacetValueGroup) {
  // console.log('filter', value, 'selected' in value)
  if ('selected' in value) {
    return value.selected === true
  } else {
    return getSelected(value.values[0])
  }
}
